import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Typography, Space, message, Popconfirm, Select, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ShopOutlined, GlobalOutlined } from '@ant-design/icons';
import { Supplier, CreateSupplierData, UpdateSupplierData, SupplierOriginType } from '../types.ts';
import { supplierAPI, supplierOriginTypeAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';

const { Title } = Typography;
const { Search } = Input;

const Suppliers: React.FC = () => {
  const { canWrite } = useAuth();
  const canEdit = canWrite('suppliers');
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [originTypes, setOriginTypes] = useState<SupplierOriginType[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [form] = Form.useForm();

  const fetchSuppliers = async (search?: string) => {
    setLoading(true);
    try {
      const data = await supplierAPI.getSuppliers(search);
      setSuppliers(data);
    } catch (error) {
      message.error('Errore nel caricamento fornitori');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
    fetchOriginTypes();
  }, []);

  const fetchOriginTypes = async () => {
    try {
      const data = await supplierOriginTypeAPI.getSupplierOriginTypes();
      setOriginTypes(data);
    } catch (error) {
      console.error('Errore nel caricamento tipi origine:', error);
    }
  };

  const handleAdd = () => {
    setEditingSupplier(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    form.setFieldsValue({
      name: supplier.name,
      supplier_code: supplier.supplier_code,
      contact_person: supplier.contact_person,
      email: supplier.email,
      phone: supplier.phone,
      address: supplier.address,
      city: supplier.city,
      postal_code: supplier.postal_code,
      country: supplier.country,
      website: supplier.website,
      notes: supplier.notes,
      origin_type_id: supplier.origin_type_id || 1,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await supplierAPI.deleteSupplier(id);
      message.success('Fornitore eliminato con successo');
      fetchSuppliers(searchTerm);
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nell\'eliminazione del fornitore');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingSupplier) {
        const updateData: UpdateSupplierData = values;
        await supplierAPI.updateSupplier(editingSupplier.id, updateData);
        message.success('Fornitore aggiornato con successo');
      } else {
        const createData: CreateSupplierData = values;
        await supplierAPI.createSupplier(createData);
        message.success('Fornitore creato con successo');
      }
      setModalVisible(false);
      fetchSuppliers(searchTerm);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Errore nell\'operazione';
      message.error(errorMessage);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    fetchSuppliers(value);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <strong>{name}</strong>,
    },
    {
      title: 'Codice Fornitore',
      dataIndex: 'supplier_code',
      key: 'supplier_code',
      render: (code: string) => code || '-',
    },
    {
      title: 'Persona di Contatto',
      dataIndex: 'contact_person',
      key: 'contact_person',
      render: (contact: string) => contact || '-',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => email || '-',
    },
    {
      title: 'Telefono',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string) => phone || '-',
    },
    {
      title: 'Città',
      dataIndex: 'city',
      key: 'city',
      render: (city: string) => city || '-',
    },
    {
      title: 'Tipo Origine',
      key: 'origin_type',
      width: 150,
      render: (_, record: Supplier) => {
        const originType = record.origin_type;
        if (!originType) return '-';

        let color = 'default';
        let icon = <GlobalOutlined />;

        switch (originType.code) {
          case 'EXTRA_UE':
            color = 'blue';
            break;
          case 'UE':
            color = 'green';
            break;
          case 'NAZIONALE':
            color = 'gold';
            break;
        }

        return (
          <Tag color={color} icon={icon}>
            {originType.description}
          </Tag>
        );
      },
    },
    {
      title: 'Sito Web',
      dataIndex: 'website',
      key: 'website',
      render: (website: string) => 
        website ? <a href={website} target="_blank" rel="noopener noreferrer">{website}</a> : '-',
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 120,
      render: (_, record: Supplier) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Sei sicuro di voler eliminare questo fornitore?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sì"
            cancelText="No"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2}>
          <ShopOutlined style={{ marginRight: 8 }} />
          Gestione Fornitori
        </Title>
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Nuovo Fornitore
          </Button>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <Search
          placeholder="Cerca per nome, contatto o email..."
          allowClear
          enterButton={<SearchOutlined />}
          size="large"
          onSearch={handleSearch}
          style={{ maxWidth: 400 }}
        />
      </div>

      <Table
        columns={canEdit ? columns : columns.filter((c) => c.key !== 'actions')}
        dataSource={suppliers}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingSupplier ? 'Modifica Fornitore' : 'Nuovo Fornitore'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        destroyOnClose
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="name"
              label="Nome Fornitore"
              rules={[{ required: true, message: 'Nome fornitore richiesto' }]}
            >
              <Input placeholder="Inserisci il nome del fornitore" />
            </Form.Item>
            
            <Form.Item
              name="supplier_code"
              label="Codice Fornitore"
            >
              <Input placeholder="Codice fornitore (opzionale)" />
            </Form.Item>
            
            <Form.Item
              name="contact_person"
              label="Persona di Contatto"
            >
              <Input placeholder="Nome della persona di contatto" />
            </Form.Item>
            
            <Form.Item
              name="email"
              label="Email"
              rules={[{ type: 'email', message: 'Email non valida' }]}
            >
              <Input placeholder="email@fornitore.com" />
            </Form.Item>
            
            <Form.Item
              name="phone"
              label="Telefono"
            >
              <Input placeholder="+39 123 456 7890" />
            </Form.Item>
            
            <Form.Item
              name="city"
              label="Città"
            >
              <Input placeholder="Milano" />
            </Form.Item>
            
            <Form.Item
              name="postal_code"
              label="CAP"
            >
              <Input placeholder="20100" />
            </Form.Item>
            
            <Form.Item
              name="country"
              label="Paese"
            >
              <Input placeholder="Italia" />
            </Form.Item>
            
            <Form.Item
              name="website"
              label="Sito Web"
            >
              <Input placeholder="https://www.fornitore.com" />
            </Form.Item>

            <Form.Item
              name="origin_type_id"
              label="Tipo Origine"
              rules={[{ required: true, message: 'Tipo origine richiesto' }]}
              initialValue={1}
            >
              <Select placeholder="Seleziona tipo origine">
                {originTypes.map((type) => (
                  <Select.Option key={type.id} value={type.id}>
                    {type.description}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          
          <Form.Item
            name="address"
            label="Indirizzo"
          >
            <Input.TextArea rows={3} placeholder="Via/Piazza, numero civico" />
          </Form.Item>
          
          <Form.Item
            name="notes"
            label="Note"
          >
            <Input.TextArea rows={4} placeholder="Note aggiuntive sul fornitore" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Suppliers;
